// Packages
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
// Redux
import { clearReducer } from '../auth/authThunk';
import { saveVisitedPageLabelAsync, getMostVisitedPageLabelsAsync } from './navThunk';
import { DEFAULT_NAV_LABELS } from './navLabels';
// Types
import { IErrorRes } from 'types/appTypes';
import { INavHistory } from 'types/userTypes';

const MAX_RECENT_PAGES = 7;

interface ISliceNavState {
  companiesLocations: boolean;
  companiesList: boolean;
  companiesContacts: boolean;
  companiesDocs: boolean;
  companiesLogins: boolean;
  locationsDocs: boolean;
  jobsList: boolean;
  jobsGroups: boolean;
  jobsRates: boolean;
  jobLog: boolean;
  jobExpenses: boolean;
  jobCandidates: boolean;
  employeeList: boolean;
  groups: boolean;
  log: boolean;
  leave: boolean;
  rates: boolean;
  docs: boolean;
  equipmentList: boolean;
  equipmentLog: boolean;
  equipmentExpenses: boolean;
  invoicesRecurring: boolean;
  invoiceRegister: boolean;
  formsPage: boolean;
  todoPage: boolean;
  checkpointsList: boolean;
  companiesContactLogs: boolean;
  navHistory: INavHistory[];
  isLoading: boolean;
  error: IErrorRes | null;
}

const initialState: ISliceNavState = {
  companiesLocations: false,
  companiesList: false,
  companiesContacts: false,
  companiesDocs: false,
  companiesContactLogs: false,
  companiesLogins: false,
  locationsDocs: false,
  jobsList: false,
  jobsGroups: false,
  jobsRates: false,
  jobLog: false,
  jobExpenses: false,
  jobCandidates: false,
  employeeList: false,
  groups: false,
  log: false,
  leave: false,
  rates: false,
  docs: false,
  checkpointsList: false,
  equipmentList: false,
  equipmentLog: false,
  equipmentExpenses: false,
  invoicesRecurring: false,
  invoiceRegister: false,
  formsPage: false,
  todoPage: false,
  navHistory: [],
  isLoading: false,
  error: null,
};

export type IPageName = keyof Omit<ISliceNavState, 'navHistory'>;

export interface IToggleNav {
  pageName: IPageName;
  shouldModalBeOpen: boolean;
}

const navSlice = createSlice({
  name: 'nav',
  initialState,
  reducers: {
    toggleModal: (state, { payload }: PayloadAction<IToggleNav>) => {
      state[payload.pageName] = payload.shouldModalBeOpen;
    },
    // The page goes first and the pages already shown stay while the visit saves and the list
    // reloads: dropping to this one page made the header blink on every page change.
    addCurrentPageToNav: (state, { payload }: PayloadAction<INavHistory>) => {
      state.navHistory = [
        payload,
        ...state.navHistory.filter(({ label }) => label !== payload.label),
      ].slice(0, MAX_RECENT_PAGES + 1);
    },
  },
  extraReducers: builder => {
    //* GET MOST POPULAR PAGES ASYNC THUNK
    builder.addCase(getMostVisitedPageLabelsAsync.pending, state => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(getMostVisitedPageLabelsAsync.fulfilled, (state, { payload }) => {
      const [currentPage] = state.navHistory;
      const withoutCurrentPage = (pages: INavHistory[]) =>
        pages.filter(({ label }) => currentPage?.label !== label).slice(0, MAX_RECENT_PAGES);
      const mostVisited = withoutCurrentPage(payload ?? []);
      // A hard load of a detail page leaves no history at all, and a new user has no visits
      // yet: both get the default pages instead of an empty nav.
      const pages = mostVisited.length
        ? mostVisited
        : withoutCurrentPage(DEFAULT_NAV_LABELS.map(label => ({ label })));

      state.isLoading = false;
      // Built anew from the answer, so a second answer (a detail page and a visit) adds nothing twice.
      state.navHistory = currentPage ? [currentPage, ...pages] : pages;
    });
    builder.addCase(getMostVisitedPageLabelsAsync.rejected, (state, { payload }) => {
      state.isLoading = false;
      state.error = payload || null;
    });

    //* SAVE MOST POPULAR PAGES ASYNC THUNK
    builder.addCase(saveVisitedPageLabelAsync.pending, state => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(saveVisitedPageLabelAsync.fulfilled, state => {
      state.isLoading = false;
    });
    builder.addCase(saveVisitedPageLabelAsync.rejected, (state, { payload }) => {
      state.isLoading = false;
      state.error = payload || null;
    });

    //* CLEAR REDUCER AFTER SIGN OUT
    builder.addCase(clearReducer, () => initialState);
  },
});

export default navSlice;

export const { toggleModal, addCurrentPageToNav } = navSlice.actions;
